import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import slotChangeRequestsAPI from "../../../../services/slot_change_requests";
import schoolsAPI from "../../../../services/schools";
import { replaceMailPreviewCidImages } from "../../../../utils/mailPreview";

const SendSlotChangeRequestParentsMailModal = forwardRef(
  ({ schoolId, trainingId, accessToken, toast, onSent }, ref) => {
    const [visible, setVisible] = useState(false);
    const [requestId, setRequestId] = useState(null);
    const [sending, setSending] = useState(false);
    const [schoolAvatar, setSchoolAvatar] = useState("");

    const previewQuery = slotChangeRequestsAPI.useGetParentsEmailPreview(
      schoolId,
      trainingId,
      accessToken,
      requestId,
      visible && !!requestId
    );
    const previewData = previewQuery.data || {};
    const previewBody = useMemo(
      () => replaceMailPreviewCidImages(previewData.body || "", schoolAvatar),
      [previewData.body, schoolAvatar]
    );
    const recipients = previewData.recipients || [];
    const hasRecipients = recipients.length > 0;
    const canSend = !previewQuery.isLoading && !previewQuery.isError && hasRecipients;

    useEffect(() => {
      if (!schoolId) {
        setSchoolAvatar("");
        return;
      }
      schoolsAPI.GetOneSchool(schoolId).then((data) => {
        setSchoolAvatar(data?.data?.avatar || "");
      });
    }, [schoolId]);

    useImperativeHandle(ref, () => ({
      show(id) {
        setRequestId(id);
        setVisible(true);
      },
      hide() {
        setVisible(false);
        setRequestId(null);
      },
    }));

    const handleSend = async () => {
      if (!requestId) {
        return;
      }
      setSending(true);
      try {
        const result = await slotChangeRequestsAPI.SendParentsEmail(
          schoolId,
          trainingId,
          accessToken,
          requestId
        );
        toast.current?.show({
          severity: "success",
          summary: "E-mail envoyé",
          detail: result?.message || "Le mail a été envoyé aux parents.",
          life: 4000,
        });
        if (onSent) {
          onSent();
        }
        setVisible(false);
        setRequestId(null);
      } catch (err) {
        toast.current?.show({
          severity: "error",
          summary: "Erreur",
          detail: err?.message || "Impossible d'envoyer le mail aux parents.",
          life: 4000,
        });
      } finally {
        setSending(false);
      }
    };

    const handleHide = () => {
      if (!sending) {
        setVisible(false);
        setRequestId(null);
      }
    };

    const dialogFooter = (
      <div className="flex justify-content-end gap-2 mt-3">
        <Button
          label="Annuler"
          icon="pi pi-times"
          className="p-button-text p-button-secondary"
          onClick={handleHide}
          disabled={sending}
        />
        <Button
          label="Envoyer"
          icon="pi pi-send"
          className="p-button-primary"
          onClick={handleSend}
          loading={sending}
          disabled={!canSend}
        />
      </div>
    );

    return (
      <Dialog
        header="Envoyer un mail aux parents"
        visible={visible}
        modal
        style={{ width: "55vw", maxWidth: "900px" }}
        contentStyle={{ overflowY: "auto", maxHeight: "70vh" }}
        footer={dialogFooter}
        onHide={handleHide}
        dismissableMask={!sending}
        closeOnEscape={!sending}
        draggable={false}
        resizable={false}
      >
        {previewQuery.isLoading ? (
          <div className="flex flex-column align-items-center justify-content-center py-6">
            <ProgressSpinner
              style={{ width: "50px", height: "50px" }}
              strokeWidth="8"
              fill="var(--surface-ground)"
              animationDuration=".5s"
            />
            <p className="mt-3 text-600 font-semibold">Génération de l'aperçu du mail...</p>
          </div>
        ) : previewQuery.isError ? (
          <div className="flex flex-column align-items-center justify-content-center py-6 text-red-500">
            <i className="pi pi-exclamation-triangle text-4xl mb-3"></i>
            <p className="font-bold">Erreur de chargement</p>
            <p className="text-sm mt-1">
              {previewQuery.error?.message || "Impossible de générer l'aperçu du mail."}
            </p>
          </div>
        ) : (
          <div className="flex flex-column gap-3">
            {!hasRecipients ? (
              <div className="p-3 border-round border-1 border-yellow-200 bg-yellow-50 text-yellow-900 text-sm">
                Aucun parent avec adresse e-mail exploitable n'a été trouvé pour cet élève.
                L'envoi est désactivé.
              </div>
            ) : null}
            <div className="p-3 bg-bluegray-50 border-round border-1 border-bluegray-100 flex flex-column gap-2 text-sm text-800">
              <div>
                <span className="font-bold">Destinataires : </span>
                <span>
                  {hasRecipients ? (
                    recipients.join(", ")
                  ) : (
                    <span className="text-red-500 italic">Aucune adresse e-mail trouvée</span>
                  )}
                </span>
              </div>
              <div>
                <span className="font-bold">Objet : </span>
                <span>{previewData.subject || "Mail aux parents"}</span>
              </div>
            </div>

            <div className="flex flex-column gap-1">
              <span className="font-bold text-sm text-600">Aperçu du contenu de l'e-mail :</span>
              <iframe
                title="Aperçu e-mail parents"
                srcDoc={previewBody}
                style={{
                  width: "100%",
                  height: "420px",
                  border: "1px solid var(--surface-border)",
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                }}
              />
            </div>
          </div>
        )}
      </Dialog>
    );
  }
);

export default SendSlotChangeRequestParentsMailModal;
